<template>
    <div :class="{'dark':darkMode.enabled, 'traditional': store.chineseMode == 'traditional', 'simplified': store.chineseMode == 'simplified'}">
        <div
            @click="$refs.nav.showMenu = false"
            class="relative from-white to-slate-50 bg-gradient-to-tr dark:from-black dark:to-gray-900 dark:selection:bg-gray-400 dark:text-gray-400 w-full max-w-screen min-h-screen print:!from-white print:!to-white"
            :class="{'has-app-tray pb-[var(--app-tray-h)] md:pb-[7rem]': !navHidden}"
        >

            <FamilyWorshipAnnouncementBar />

            <OnsiteBanner />

            <Nav :hidden="navHidden" ref="nav" class="" />

            <div id="mobile-nav-spacer" class="print:hidden md:hidden w-full flex justify-center py-6 opacity-0 transition-all duration-base ease-standard overflow-hidden" :class="navHidden ? 'max-h-0 !py-0' : 'max-h-20'">
                <router-link @click="store.openType = 'slideRight'" to="/" ><Logo alt="Logo" class="fill-gray-300 w-20" /></router-link>
            </div>
            <div id="mobile-nav-header" class="print:hidden md:hidden w-full justify-between flex items-center py-6 pt-[max(1.5rem,env(safe-area-inset-top))] fixed top-0 left-0 z-50 backdrop-blur-xl px-4 text-xs transition-all duration-base ease-standard" :class="navHidden ? 'opacity-0 pointer-events-none -translate-y-full' : ''">
				<button
                    to="/"
					@click="goBack"
					:class="{
                        'opacity-0 pointer-events-none': $route.path == '/',
                        '!w-[5.5rem]': store.cartTotalItems
                    }"
					class="w-14 h-6 pl-6 rounded-full flex items-center justify-start text-left"
					aria-label="Back"
                >
                    <i
						aria-hidden="true"
						class="fa dark:text-white/50"
						:class="store.openTypeApplied == 'slideUp' ? 'fa-times' : 'fa-chevron-left'"
                    />
                </button>

                <router-link @click="store.openType = 'slideRight'" to="/" ><Logo alt="Logo" class="dark:fill-gray-300 fill-black w-20 dark:drop-shadow-[0_0_20px_rgb(0,0,0)] dark:drop-shadow-[0_0_12px_rgb(0,0,0)]" /></router-link>

                <div class="flex items-center gap-2">
                    <router-link to="/cart" v-if="store.cartTotalItems" aria-label="Cart" class="relative text-center text-xl w-6 h-6 flex items-center justify-center">
                        <i class="fal fa-shopping-cart text-ncfic-blue-700 dark:text-ncfic-blue-50" aria-hidden="true" />
                        <div class="w-4 h-4 bg-ncfic-red-500 text-white text-2xs rounded-full absolute -top-1 -left-1 flex items-center justify-center" v-html="store.cartTotalItems" />
                    </router-link>
                    <!-- HAP-4295: the phone header had no light/dark switch (client
                         report). Shown only on the sermons redesign preview for now.
                         HAP-4419: also on the ?preview=9 individual sermon pages. -->
                    <button v-if="showPhoneThemeToggle" @click="darkMode.toggle" :aria-label="darkMode.enabled ? 'Switch to light mode' : 'Switch to dark mode'" class="text-xl w-6 h-6 flex items-center justify-center"><i class="fad" :class="darkMode.enabled ? 'fa-sun' : 'fa-moon-stars'" aria-hidden="true" /></button>
                    <router-link to="/account" aria-label="Account" class="text-xl w-6 h-6 flex items-center justify-center"><i class="fad fa-user" aria-hidden="true" /></router-link>
                    <button @click="store.hamburgerMenu = !store.hamburgerMenu" aria-label="Menu" :aria-expanded="store.hamburgerMenu ? 'true' : 'false'" class="text-xl w-6 h-6 flex items-center justify-center"><i class="fal fa-bars" aria-hidden="true" /></button>
                    <!-- <button @click="darkMode.toggle" class="text-xl w-6 h-6"><i class="fad" :class="!darkMode.enabled ? 'fa-sun' : 'fa-moon-stars mb-2'" /></button> -->
                </div>
            </div>

            <!-- a11y: the single <main> landmark for every routed page. `contents` keeps
                 the box out of layout so the page renders exactly as before. -->
            <main id="main-content" class="contents">
            <RouterView v-slot="{ Component, route }" @navHidden="navHidden = $event" >
                <template v-if="Component">
                    <!-- <KeepAlive v-if="route.path.includes('resources')">
                        <Suspense :timeout="200">
                            <Component :is="Component" :key="route.meta.usePathKey ? route.path : undefined" />
                            <template #fallback><spinner /></template>
                        </Suspense>
                    </KeepAlive> -->
                    <Suspense :timeout="1" >
                        <Component :is="Component" :key="routeKey(route)" :class="(!breakpoints.md && !store.viewTransitions) ? animations[store.openTypeApplied] : ''" v-mounted @mounted="store.scrollCallback" />
                        <template #fallback><PageSkeleton :path="route.path" /></template>
                    </Suspense>
                </template>
                <!-- HAP-5052: shown while the first navigation waits on the user load (checkout refresh) -->
                <Spinner v-else />
            </RouterView>
            </main>


            <MiniPlayer v-if="store.nowPlaying?.id" class="fixed bottom-[var(--app-tray-h)] left-0 z-[51]" />

            <!-- HAP-4591: the audiobook plays on across pages, with its controls at the foot -->
            <AudiobookPlayerBar />

            <Footer v-show="!navHidden" class="absolute bottom-0 hidden md:flex" />

            <Modal
              v-if="allowedVideos.includes(promoVideo) || allowedYouTubeVideos.includes(promoVideo)"
              @hide="$router.push({ query: {} })"
              maxWidth="max-w-4xl !p-0"
            >
              <!-- YouTube -->
              <iframe
                v-if="isNaN(promoVideo) && allowedYouTubeVideos.includes(promoVideo)"
                :src="`https://www.youtube.com/embed/${promoVideo}?autoplay=1&rel=0&modestbranding=1`"
                class="w-full aspect-video"
              />

              <!-- Vimeo -->
              <iframe
                v-else-if="allowedVideos.includes(promoVideo)"
                :src="`https://player.vimeo.com/video/${promoVideo}?h=${promoVideoHash}&color=6585a3&byline=0&portrait=0&title=0&autoplay=1`"
                class="w-full aspect-video"
              />
            </Modal>


            <AppDrawer v-show="!store.hamburgerMenu && !store.hideAppTray" :class="{hidden: navHidden}" class="md:hidden print:hidden" />

            <FamilyWorshipSlideinPopup />
            <FamilyWorshipExitIntentPopup />
            <FamilySingsBookPopup />

            <!-- Charlie Kirk -->
            <Modal class="w-full !z-[999]" max-width="max-w-4xl" v-if="showCharlie" @hide="closeCharlie">
                <div class="text-4xl font-bold text-center">Charlie Kirk</div>
                <div class="text-xl text-center !mt-2">1993-2025</div>
                <div class="w-full aspect-video bg-cover bg-center mx-auto rounded-lg !mt-4" style="background-image: url(https://cfl-mango.s3.amazonaws.com/uploads/about/charlie-kirk.jpg)"></div>
                <div>One of the saddest moments in memory. Charlie Kirk was one of the strongest, the most gracious, the most explicitly Christian voices in the public square in my lifetime. It is painful to know that he will not be with us in the battles ahead.</div>
                <!-- <div>Scott Brown</div> -->
                <img src="https://cfl-mango.s3.amazonaws.com/uploads/about/signature.png" class="w-1/2 max-w-32 !mt-4 dark:invert" />
            </Modal>

        </div>
    </div>
</template>

<script>
import Nav from './components/layout/nav.vue'
import MiniPlayer from './components/layout/mini-player.vue'
import AudiobookPlayerBar from './components/audiobook/playerBar.vue'
import Footer from './components/layout/footer.vue'
import Modal from './components/layout/modal.vue'
import AppDrawer from './components/layout/appDrawer.vue'
import OnsiteBanner from './components/layout/onsite-banner.vue'
import FamilyWorshipSlideinPopup from './components/layout/family-worship-slidein-popup.vue'
import FamilyWorshipExitIntentPopup from './components/layout/family-worship-exit-intent-popup.vue'
import FamilySingsBookPopup from './components/layout/family-sings-book-popup.vue'
import FamilyWorshipAnnouncementBar from './components/layout/family-worship-announcement-bar.vue'
import Logo from './assets/logo.vue'
import PageSkeleton from './components/layout/skeletons/page-skeleton.vue'

// Function for setting cookies
let setCookie = function (cname, cvalue) {
    var d = new Date();
    d.setTime(d.getTime() + (365 * 24 * 60 * 60 * 1000));
    var expires = "expires=" + d.toUTCString();
    document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
}

export default {
    components: {Nav, MiniPlayer, AudiobookPlayerBar, Footer, Modal, AppDrawer, OnsiteBanner, FamilyWorshipSlideinPopup, FamilyWorshipExitIntentPopup, FamilySingsBookPopup, FamilyWorshipAnnouncementBar, Logo, PageSkeleton},
    inject: ['store','darkMode','axios','breakpoints'],
    data() {
        return {
            showCharlie: false,//(!(window.location.href.includes('embed') || window.location.href.includes('sermons')) && window.localStorage.getItem('showCharlie') !== 'false'),
            // Fallback enter animations for browsers without View Transitions
            // (HAP-1887). Transform/opacity only — the previous `motion-blur-in`
            // was dropped because filter:blur creates a containing block that
            // traps position:fixed modals (memory cfl_motion_filter_modal_gotcha)
            // and is more expensive than compositor-friendly transform/opacity.
            animations: {
                default: '',
                slideLeft: 'motion-translate-x-in-[100%] motion-opacity-in-[0%] motion-duration-[0.35s] motion-duration-[0.53s]/translate',
                slideRight: 'motion-translate-x-in-[-100%] motion-opacity-in-[0%] motion-duration-[0.35s] motion-duration-[0.53s]/translate',
                slideUp: 'motion-translate-y-in-[100%] motion-opacity-in-[0%] motion-duration-[0.35s] motion-duration-[0.53s]/translate',
                slideDown: 'motion-translate-y-in-[-100%] motion-opacity-in-[0%] motion-duration-[0.35s] motion-duration-[0.53s]/translate',
                popIn: 'motion-scale-in-[0.5] motion-translate-y-in-[-25%] motion-opacity-in-[0%] motion-duration-[0.53s] motion-duration-[0.53s]/scale',
            },
            navHidden: false,
            allowedYouTubeVideos: [
                'hgdfxhtQnUA',
                'XSqpaMZonCo',
                'lvPqZZn0x7o',
                'jn_NJYGZ08g',
                'LnaGcquC7MU', // HAP-4579 "70% of your children walk away" — cf.life/70percent
                'i5P8Pa40avE', // HAP-4593 "Everyday Faithfulness" — cf.life/quietfaithfulness
            ],
            allowedVideos: [
                '863273014',
                '812567580',
                '809150337',
                '810305788',
                '522413191',
                '709198735',
                '844611074',
                '853107156',
                '841278292',
                '867965434', // 20 day challenge
                '863329214', // 20 day challenge
                '863278705', // 20 day challenge
                '863329058', // 20 day challenge
                '863271654', // 20 day challenge
                '863329102', // 20 day challenge
                '863272250', // 20 day challenge
                '863329138', // 20 day challenge
                '863272722', // 20 day challenge
                '863329182', // 20 day challenge
                '870301889',
                '870339299',
                '899912236',
                '905332298',
                '905762913',
                '907614467',
                '906488537',
                '910902056',
                '911237721',
                '914526306',
                '914515554',
                '921262946',
                '922956436',
                '928560872',
                '928416101',
                '941702259',
                '953587663',
                '963261389',
                '952424415',
                '982171830',
                '1023047830',
                '1033922216',
                '1033919736',
                '1033940257',
                '1040800496',
                '1014523414',
                '1013299449',
                '1053449559',
                '1065868878',
                '1060120035',
                '1071562209',
                '1073674675',
                '1076065588',
                '1072335270',
                '1098273257',
                '57242982',
                '1135856323',
                '1137040498',
                '1143608051',
                '896669228',
                '1145386699',
                '1186354003',
                '1210836385', // Restore Family Worship intro
                '1200559158', // The Weeks Family (donor story, HAP-3655)
                '1200558915', // Joel Carlson (donor story, HAP-3655)
            ],
            // Most of the videos above share one Vimeo privacy hash. Newer
            // unlisted uploads each get their own, and the player 403s if the
            // hash doesn't match the id, so override them here (HAP-3655).
            videoHashes: {
                '1200559158': '6b417670d0',
                '1200558915': '86a6faf360',
            },
            defaultVideoHash: '68e999d930',
        }
    },
    watch: {
        loggedIn: {
            handler() {
                const token = new URLSearchParams(window.location.search).get("token");
                // Save credentials for people who are already logged in through a qs or something
                if (this.loggedIn && token) {
                    setCookie(`Authorization`, token)
                    this.axios.defaults.headers.common['Authorization'] = token

                    // console.log('this.store.user', this.store.user)

                    window.localStorage.setItem('user', this.store.user?.member?.id)
                    window.localStorage.setItem('email', this.store.user?.member?.email)

                    // this.$router.push({path: this.$route.path, query: {}})
                    window.history.replaceState({}, document.title, window.location.pathname);
                    setTimeout(() => {window.history.replaceState({}, document.title, window.location.pathname);}, 500);
                    setTimeout(() => {window.history.replaceState({}, document.title, window.location.pathname);}, 1000);
                    setTimeout(() => {window.history.replaceState({}, document.title, window.location.pathname);}, 1500);
                    setTimeout(() => {window.history.replaceState({}, document.title, window.location.pathname);}, 2000);

                }
            },
            immediate: true
        },
        navHiddenFromRoute: {
            handler() {
                this.navHidden = this.navHiddenFromRoute
            },
            immediate: true
        },
        // navHidden: {
        //     handler() {
        //         if (this.navHidden) Tawk_API?.hideWidget?.();
        //         setTimeout(() => {
        //             if (this.navHidden) Tawk_API?.hideWidget?.();
        //         }, 1000);
        //         setTimeout(() => {
        //             if (this.navHidden) Tawk_API?.hideWidget?.();
        //         }, 2000);
        //     },
        //     immediate: true
        // }
    },
    // mounted() {
    //     if (this.$route.query?.token) {
    //         let tokenless = this.$route.query
    //         delete tokenless.token
    //         tokenless = Object.keys(tokenless).length ? tokenless : ''
    //         console.log('remove token:', tokenless)
    //         this.$router.push({query: tokenless})
    //     }
    // },
    methods: {
        // HAP-4214: the routed shell is keyed for the top-level <RouterView>. The church
        // dashboard is a NESTED route (church-hub shell + child tabs). Keying by the full
        // route.path meant every profile-wizard "Save & Continue" — which pushes
        // .../profile/<nextTab> — changed route.path and so DESTROYED + REMOUNTED the whole
        // dashboard shell: `church` reset to null, `v-if="church"` hid the hero/nav/content,
        // the document collapsed so the page snapped to the very top (hero revealed), then
        // the shell remounted and its v-motion icon-nav entrance animation replayed. That
        // remount — a layer ABOVE the wizard — is the "sections jumping between steps" the
        // client kept reporting, and is why every in-wizard scroll fix failed (church-create
        // was being torn down each step). Fix, preview-gated so prod stays byte-identical:
        // inside the dashboard with preview on, key by the top-level route RECORD (params
        // substituted in) so the shell stays mounted across child tab changes and the wizard
        // cross-fades in place; everywhere else keep the existing full-path key.
        routeKey(route) {
            try {
                const previewOn = !!route.query?.preview || !!window.sessionStorage.getItem('preview')
                const inDashboard = /^\/network\/[^/]+\/dashboard/.test(route.path)
                if (previewOn && inDashboard) {
                    const rec = route.matched?.[0]
                    if (rec?.path) return rec.path.replace(/:(\w+)/g, (_, p) => route.params?.[p] ?? '')
                }
            } catch (e) { /* fall through to default keying */ }
            return route.path
        },
        goBack() {
            this.store.openType = (this.store.openTypeApplied == 'slideUp') ? 'slideDown' : 'slideRight'
            this.$router.go(-1)
            // Scroll up
            // window.scrollTo({top: 0})
        },
        closeCharlie() {
            this.showCharlie = false
            window.localStorage.setItem('showCharlie', 'false')
        }
    },
    computed: {
        // Phone-header light/dark switch: the sermons list preview (?preview=2,
        // HAP-4295) and every individual sermon page (HAP-4419, public 2026-09-30
        // via HAP-4518).
        showPhoneThemeToggle() {
            if (String(this.$route.query?.preview ?? '') === '2') return true
            // HAP-4295 went public: the whole sermons area (listing, catalogue
            // pages, individual sermons) gets the phone light/dark switch.
            return /^\/sermons(\/|$)/.test(this.$route.path || '')
        },
        loggedIn() { return !!this.store.user?.member?.id },
        token() { return this.$route.query?.token },
        promoVideo() { return this.$route.query?.video },
        promoVideoHash() { return this.videoHashes[this.promoVideo] || this.defaultVideoHash },
        navHiddenFromRoute() { return !!this.$route.query?.navHidden },
        showDonate() {
            let paths = [
                // '/fsr',
                // '/20days',
                // '/marriage',
                // '/ridgecrest24',
                // '/church-network',
                // '/scripture-applied',
                // '/events/65f06e8db04a89346884a83b',
                // '/products/656a651a6a24bc0453a6d54b',
                // '/products/656a6583c33a2766fdd75b97',
            ]
            return paths.includes(this.$route.path)
        },
        donateSmall()  {
            let paths = [
                '/20days',
                '/church-network',
                '/events/65f06e8db04a89346884a83b',
            ]
            return paths.includes(this.$route.path)
        }
    }
}

// Fix for big S3 outage :fp
// let invalidSources = ['https://cfl-mango.s3.amazonaws.com', 'https://cfl-mango.s3.us-east-1.amazonaws.com']
// setInterval(() => {
//     // Replace every instance of https://cfl-mango.s3.us-east-1.amazonaws.com on the page with https://files.cf.life/s3
//     document.querySelectorAll('img').forEach(img => {
//         if (img.src && invalidSources.some(src => img.src.includes(src))) {
//             img.src = img.src.replace(invalidSources[0], 'https://files.cf.life/S3-Mango')
//             img.src = img.src.replace(invalidSources[1], 'https://files.cf.life/S3-Mango')
//         }
//     })
//     document.querySelectorAll('video').forEach(video => {
//         if (video.src && invalidSources.some(src => video.src.includes(src))) {
//             video.src = video.src.replace(invalidSources[0], 'https://files.cf.life/S3-Mango')
//             video.src = video.src.replace(invalidSources[1], 'https://files.cf.life/S3-Mango')
//         }
//     })
//     // Audio
//     document.querySelectorAll('audio').forEach(audio => {
//         if (audio.src && invalidSources.some(src => audio.src.includes(src))) {
//             audio.src = audio.src.replace(invalidSources[0], 'https://files.cf.life/S3-Mango')
//             audio.src = audio.src.replace(invalidSources[1], 'https://files.cf.life/S3-Mango')
//         }
//     })
//     // Div with backgroudn image
//     document.querySelectorAll('div').forEach(div => {
//         if (div.style.backgroundImage && invalidSources.some(src => div.style.backgroundImage.includes(src))) {
//             div.style.backgroundImage = div.style.backgroundImage.replace(invalidSources[0], 'https://files.cf.life/S3-Mango')
//             div.style.backgroundImage = div.style.backgroundImage.replace(invalidSources[1], 'https://files.cf.life/S3-Mango')
//         }
//     })
// }, 100)
</script>

<style lang="postcss">
@import url('https://fonts.googleapis.com/css2?family=Inconsolata&display=swap');
/* Reset default styles for the select element */
/* Reset default styles for the select element */
select {
  appearance: none; /* Remove default appearance on some browsers */
  -webkit-appearance: none; /* Remove default appearance on Safari */
  -moz-appearance: none; /* Remove default appearance on Firefox */
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23000' height='24' viewBox='0 0 24 24' width='24'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3Cpath d='M0 0h24v24H0z' fill='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0px center; /* Adjust the position of the arrow */
}

/* Apply the filter utility to change SVG color in dark mode */
.dark select {
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='rgba(255, 255, 255, 0.5)' height='24' viewBox='0 0 24 24' width='24'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3Cpath d='M0 0h24v24H0z' fill='none'/%3E%3C/svg%3E");
}
.traditional { font-variant-east-asian: traditional; }
.simplified { font-variant-east-asian: simplified; }

/* When the Family Worship announcement bar is visible, push the sticky
   desktop nav and the fixed mobile nav header below it so they stack
   instead of overlapping. The bar height is ~2.25rem mobile / ~2.5rem desktop. */
body.has-fw-announcement-bar .mainnav { top: 2.25rem; }
body.has-fw-announcement-bar #mobile-nav-header { top: 2.25rem; }
@media (min-width: 640px) {
    body.has-fw-announcement-bar .mainnav { top: 2.5rem; }
    body.has-fw-announcement-bar #mobile-nav-header { top: 2.5rem; }
}
</style>
